// Formatted text: bold, italic, underline, strike, a font or size on some words, bullet and numbered
// lists, small headings, quotes and links. One format for every place an owner writes formatted text
// (a Rich Text section's paragraphs, a blog post's body, a community's descriptions): a small set of
// HTML tags, the same tags the Studio's text editor writes.
//
// The live site and the Studio preview never put this text into the page as HTML. It is read here
// into a tree and drawn as React elements, keeping only the tags and attributes listed below: a link
// only when its address is a page of the site, a web address, a phone or an email (the same four kinds
// a table cell's link takes, tableCellLinks.ts); a picture only from https or the site itself; a font
// or size only from the short lists below. Everything else (a script, a style, an onclick, an iframe)
// is dropped, its words kept where they are words. Text with no tags at all reads as plain text, as
// it always has.

import { Fragment, createElement, type CSSProperties, type ReactNode } from 'react';
import { cellLinkKind } from './blocks/tableCellLinks';

/** The fonts words can take. `heading`/`body` follow the site's brand fonts. */
export const RICH_FONTS = {
  heading: { label: 'Heading font', css: 'var(--font-heading, inherit)' },
  body: { label: 'Body font', css: 'var(--font-body, inherit)' },
  serif: { label: 'Serif', css: "Georgia, 'Times New Roman', serif" },
  sans: { label: 'Sans serif', css: 'Arial, Helvetica, sans-serif' },
} as const;
export type RichFont = keyof typeof RICH_FONTS;

/** The sizes words can take, relative to the text around them. */
export const RICH_SIZES = {
  sm: { label: 'Small', css: '0.875em' },
  lg: { label: 'Large', css: '1.25em' },
  xl: { label: 'Extra large', css: '1.5em' },
} as const;
export type RichSize = keyof typeof RICH_SIZES;

export type RichNode =
  | { t: 'text'; v: string }
  | { t: 'el'; tag: string; attrs: Record<string, string>; kids: RichNode[] };

const KEPT = new Set([
  'p', 'br', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins', 'sup', 'sub', 'code', 'pre',
  'ul', 'ol', 'li', 'a', 'img', 'span', 'blockquote',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'figure', 'figcaption',
  'div', 'section', 'article',
]);
const VOID = new Set(['br', 'hr', 'img']);
/** Dropped with everything inside them. */
const DROPPED = new Set(['script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template', 'svg', 'math', 'head', 'title', 'textarea', 'select', 'button']);
const BLOCK = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'table', 'figure', 'div', 'section', 'article', 'pre', 'hr']);

/** Tags a value must hold for it to be read as formatted text rather than plain words. */
const MARKUP = /<\/?(?:p|br|hr|h[1-6]|strong|b|em|i|u|s|del|ins|sup|sub|code|pre|ul|ol|li|a|img|span|blockquote|table|thead|tbody|tfoot|tr|th|td|caption|figure|figcaption|div|section|article)(?:\s[^<>]*)?\/?>/i;

/** True when a value is formatted text (holds one of the tags above), false for plain words. */
export function hasRichMarkup(value: string | null | undefined): boolean {
  return typeof value === 'string' && MARKUP.test(value);
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#39': "'" };
function decode(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+[0-9]*);/gi, (m, e: string) => {
    const k = e.toLowerCase();
    if (k in ENTITIES) return ENTITIES[k];
    const code = k.startsWith('#x') ? parseInt(k.slice(2), 16) : k.startsWith('#') ? parseInt(k.slice(1), 10) : NaN;
    return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : m;
  });
}
const escText = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/ /g, '&nbsp;');
const escAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const TAG = /<!--[\s\S]*?-->|<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/g;
const ATTR = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;

function readAttrs(raw: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const m of Array.from(raw.matchAll(ATTR))) out[m[1].toLowerCase()] = decode(m[2] ?? m[3] ?? m[4] ?? '');
  return out;
}

/** A page of the site, a web address, a phone, an email, or a spot on the same page (#contact). */
function safeHref(href: string | undefined): string | null {
  const h = (href ?? '').trim();
  if (!h) return null;
  if (/^#[A-Za-z][\w-]*$/.test(h)) return h;
  return cellLinkKind(h) ? h : null;
}
function safeSrc(src: string | undefined): string | null {
  const s = (src ?? '').trim();
  if (/^https:\/\/[^\s"'<>]+$/i.test(s)) return s;
  if (/^\/(?!\/)[^\s"'<>]*$/.test(s)) return s;
  return null;
}
const isFont = (v: string | undefined): v is RichFont => !!v && Object.prototype.hasOwnProperty.call(RICH_FONTS, v);
const isSize = (v: string | undefined): v is RichSize => !!v && Object.prototype.hasOwnProperty.call(RICH_SIZES, v);

/** Keep only the attributes a tag is allowed, with values that are safe. */
function keepAttrs(tag: string, a: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  if (tag === 'a') {
    const href = safeHref(a.href);
    if (href) {
      out.href = href;
      if (a.target === '_blank' && cellLinkKind(href) === 'web') out.target = '_blank';
    }
  } else if (tag === 'img') {
    const src = safeSrc(a.src);
    if (src) out.src = src;
    if (a.alt) out.alt = a.alt;
  } else if (tag === 'span') {
    if (isFont(a['data-font'])) out['data-font'] = a['data-font'];
    if (isSize(a['data-size'])) out['data-size'] = a['data-size'];
  } else if (tag === 'th' || tag === 'td') {
    for (const k of ['colspan', 'rowspan']) if (/^[1-9][0-9]?$/.test(a[k] ?? '')) out[k] = a[k];
  } else if (tag === 'ol') {
    if (/^-?[0-9]{1,6}$/.test(a.start ?? '')) out.start = a.start;
  }
  return out;
}

/** Read formatted text into a tree of kept tags and words. Never throws; unknown tags keep their words. */
export function parseRichText(html: string): RichNode[] {
  const root: RichNode = { t: 'el', tag: '#root', attrs: {}, kids: [] };
  const stack: Array<Extract<RichNode, { t: 'el' }>> = [root as Extract<RichNode, { t: 'el' }>];
  let at = 0;
  let skipping: string | null = null;
  const pushText = (s: string) => {
    if (!s || skipping) return;
    const top = stack[stack.length - 1];
    const last = top.kids[top.kids.length - 1];
    const v = decode(s);
    if (last && last.t === 'text') last.v += v;
    else top.kids.push({ t: 'text', v });
  };
  for (const m of Array.from(html.matchAll(TAG))) {
    pushText(html.slice(at, m.index));
    at = (m.index ?? 0) + m[0].length;
    if (!m[2]) continue; // a comment
    const closing = m[1] === '/';
    const tag = m[2].toLowerCase();
    if (skipping) {
      if (closing && tag === skipping) skipping = null;
      continue;
    }
    if (DROPPED.has(tag)) {
      if (!closing && !/\/\s*$/.test(m[3])) skipping = tag;
      continue;
    }
    if (!KEPT.has(tag)) continue; // unknown tag: drop the tag, keep its words
    if (closing) {
      const i = stack.map((n) => n.tag).lastIndexOf(tag);
      if (i > 0) stack.length = i;
      continue;
    }
    const el: Extract<RichNode, { t: 'el' }> = { t: 'el', tag, attrs: keepAttrs(tag, readAttrs(m[3])), kids: [] };
    // A new list item or paragraph closes an open one, as a browser would.
    if (tag === 'li' || tag === 'p') {
      const open = stack.map((n) => n.tag).lastIndexOf(tag);
      const listAbove = Math.max(stack.map((n) => n.tag).lastIndexOf('ul'), stack.map((n) => n.tag).lastIndexOf('ol'));
      if (open > 0 && (tag === 'p' || open > listAbove)) stack.length = open;
    }
    stack[stack.length - 1].kids.push(el);
    if (!VOID.has(tag)) stack.push(el);
  }
  pushText(html.slice(at));
  return root.kids;
}

/** The tree back to clean HTML: only kept tags and attributes. */
export function serializeRichText(nodes: RichNode[]): string {
  return nodes
    .map((n) => {
      if (n.t === 'text') return escText(n.v);
      const attrs = Object.entries(n.attrs).map(([k, v]) => ` ${k}="${escAttr(v)}"`).join('');
      if (VOID.has(n.tag)) return `<${n.tag}${attrs}>`;
      return `<${n.tag}${attrs}>${serializeRichText(n.kids)}</${n.tag}>`;
    })
    .join('');
}

/** Formatted text with everything unsafe removed. Plain words come back as they are. */
export function cleanRichText(value: string): string {
  return hasRichMarkup(value) ? serializeRichText(parseRichText(value)) : value;
}

/** The words a reader sees, for a card, a meta description, a summary or a fact check. */
export function richTextPlain(value: string | null | undefined): string {
  if (!value) return '';
  if (!hasRichMarkup(value)) return value;
  const out: string[] = [];
  const walk = (nodes: RichNode[]) => {
    for (const n of nodes) {
      if (n.t === 'text') out.push(n.v);
      else {
        if (n.tag === 'br') out.push('\n');
        if (n.tag === 'li') out.push('\n• ');
        walk(n.kids);
        if (BLOCK.has(n.tag) && n.tag !== 'li') out.push('\n');
      }
    }
  };
  walk(parseRichText(value));
  return out.join('').replace(/ /g, ' ').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

function toReact(nodes: RichNode[], keyBase = ''): ReactNode[] {
  return nodes.map((n, i) => {
    const key = `${keyBase}${i}`;
    if (n.t === 'text') return n.v;
    const kids = toReact(n.kids, `${key}.`);
    const a = n.attrs;
    switch (n.tag) {
      case 'a':
        if (!a.href) return createElement(Fragment, { key }, ...kids);
        return createElement('a', { key, href: a.href, ...(a.target ? { target: '_blank', rel: 'noopener noreferrer' } : {}) }, ...kids);
      case 'img':
        return a.src ? createElement('img', { key, src: a.src, alt: a.alt ?? '', loading: 'lazy', style: { maxWidth: '100%', height: 'auto' } }) : null;
      case 'span': {
        const style: CSSProperties = {};
        if (isFont(a['data-font'])) style.fontFamily = RICH_FONTS[a['data-font']].css;
        if (isSize(a['data-size'])) style.fontSize = RICH_SIZES[a['data-size']].css;
        return Object.keys(style).length ? createElement('span', { key, style }, ...kids) : createElement(Fragment, { key }, ...kids);
      }
      case 'ul':
        return createElement('ul', { key, style: { listStyleType: 'disc', paddingLeft: '1.5em', margin: '0 0 1em' } }, ...kids);
      case 'ol':
        return createElement('ol', { key, start: a.start ? Number(a.start) : undefined, style: { listStyleType: 'decimal', paddingLeft: '1.5em', margin: '0 0 1em' } }, ...kids);
      case 'th':
      case 'td':
        return createElement(n.tag, { key, colSpan: a.colspan ? Number(a.colspan) : undefined, rowSpan: a.rowspan ? Number(a.rowspan) : undefined }, ...kids);
      case 'br':
      case 'hr':
        return createElement(n.tag, { key });
      case 'b':
        return createElement('strong', { key }, ...kids);
      case 'i':
        return createElement('em', { key }, ...kids);
      case 'section':
      case 'article':
        return createElement('div', { key }, ...kids);
      default:
        return createElement(n.tag, { key }, ...kids);
    }
  });
}

/** True when the value starts with a block (a list, a heading, a paragraph), so it can't sit inside a <p>. */
function holdsBlocks(nodes: RichNode[]): boolean {
  return nodes.some((n) => n.t === 'el' && BLOCK.has(n.tag));
}

/**
 * One piece of formatted text, drawn safely. Plain words draw as one paragraph per blank-line gap
 * (`plain="paragraphs"`) or as they are (`plain="inline"`).
 */
export function RichText({
  value,
  className,
  style,
  paragraphClassName,
  plain = 'paragraphs',
}: {
  value: string | null | undefined;
  className?: string;
  style?: CSSProperties;
  /** Each paragraph's class, for plain words and for a value whose words sit outside any block. */
  paragraphClassName?: string;
  plain?: 'paragraphs' | 'inline';
}) {
  const v = value ?? '';
  if (!hasRichMarkup(v)) {
    if (plain === 'inline') return createElement('p', { className: paragraphClassName ?? className, style }, v);
    return createElement(
      'div',
      { className, style },
      ...v.split(/\n{2,}/).filter((p) => p.trim()).map((p, i) => createElement('p', { key: i, className: paragraphClassName }, p)),
    );
  }
  const nodes = parseRichText(v);
  if (!holdsBlocks(nodes)) {
    return createElement('div', { className: `blk-rich${className ? ` ${className}` : ''}`, style }, createElement('p', { className: paragraphClassName }, ...toReact(nodes)));
  }
  return createElement('div', { className: `blk-rich${className ? ` ${className}` : ''}`, style }, ...toReact(nodes));
}

/**
 * One item of a Rich Text section's paragraphs: plain words or inline formatting draw as a <p>;
 * a list, heading or quote draws as itself (it can't sit inside a <p>).
 */
export function RichParagraph({ value, className }: { value: string; className?: string }) {
  if (!hasRichMarkup(value)) return createElement('p', { className }, value);
  const nodes = parseRichText(value);
  if (holdsBlocks(nodes)) return createElement('div', { className: `blk-rich${className ? ` ${className}` : ''}` }, ...toReact(nodes));
  return createElement('p', { className }, ...toReact(nodes));
}

// ── A Rich Text section's paragraphs ⇄ one editor document ──────────────────────────────────────────
// The section keeps its paragraphs as a list (the store BLT, the planner and the forms already write):
// each item is plain words, a paragraph's inline formatting, or one list / heading / quote. The editor
// edits them as one document.

/** The paragraphs as one document for the editor. */
export function paragraphsToRichText(paragraphs: string[]): string {
  return paragraphs
    .filter((p) => typeof p === 'string')
    .map((p) => {
      if (!hasRichMarkup(p)) return `<p>${escText(p)}</p>`;
      const nodes = parseRichText(p);
      return holdsBlocks(nodes) ? serializeRichText(nodes) : `<p>${serializeRichText(nodes)}</p>`;
    })
    .join('');
}

/** The editor's document back to paragraphs: a paragraph with no formatting is saved as plain words. */
export function richTextToParagraphs(html: string): string[] {
  const out: string[] = [];
  for (const n of parseRichText(html)) {
    if (n.t === 'text') {
      if (n.v.trim()) out.push(n.v);
      continue;
    }
    if (n.tag === 'p') {
      const plainOnly = n.kids.every((k) => k.t === 'text');
      const s = plainOnly ? n.kids.map((k) => (k.t === 'text' ? k.v : '')).join('').replace(/ /g, ' ') : serializeRichText(n.kids);
      if (richTextPlain(s).trim()) out.push(s);
      continue;
    }
    if (n.tag === 'br') continue;
    const s = serializeRichText([n]);
    if (richTextPlain(s).trim() || n.tag === 'hr' || n.tag === 'img') out.push(s);
  }
  return out.length ? out : [''];
}
